/** Side-by-side OCR verification modal — image (zoom/rotate) + editable form. */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, ZoomIn, ZoomOut, RotateCw, Check, Ban } from "lucide-react";

const TEXT_FIELDS = [
  { key: "station_name",   label: "Benzinska postaja",  ph: "INA, Petrol, Shell…" },
  { key: "invoice_number", label: "Broj računa",        ph: "12/POS1/1" },
  { key: "jir",            label: "JIR",                ph: "36 znakova", mono: true },
  { key: "zki",            label: "ZKI",                ph: "32 znaka",   mono: true },
  { key: "issuer_oib",     label: "OIB Izdavatelja",    ph: "11 znamenki" },
  { key: "buyer_oib",      label: "OIB Kupca",          ph: "11 znamenki" },
  { key: "time",           label: "Vrijeme",            ph: "14:35" },
  { key: "fuel_type",      label: "Vrsta goriva",       ph: "Eurodizel" },
];

const inputCls =
  "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 " +
  "focus:outline-none focus:border-[#C5A059]/60 [color-scheme:dark]";

export const ReceiptVerifyModal = ({ receipt, photoUrl, onClose, onSaved }) => {
  const ocr = receipt.ocr || {};
  const [form, setForm] = useState({
    receipt_date: receipt.receipt_date || "",
    amount_eur: receipt.amount_eur ?? "",
    vat_amount_eur: ocr.vat_amount_eur ?? "",
    payment_method: ocr.payment_method || "",
    accounting_name: receipt.accounting_name || "",
    ...Object.fromEntries(TEXT_FIELDS.map((f) => [f.key, ocr[f.key] || ""])),
  });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const ocrPayload = () => ({
    ...Object.fromEntries(TEXT_FIELDS.map((f) => [f.key, form[f.key] || null])),
    payment_method: form.payment_method || null,
    vat_amount_eur: form.vat_amount_eur === "" ? null : Number(form.vat_amount_eur),
  });

  const approveAndSave = async () => {
    const amount = Number(form.amount_eur);
    if (!amount || amount <= 0) return toast.error("Unesite ispravan ukupni iznos (€).");
    setBusy(true);
    try {
      await api.patch(`/admin/fuel-receipts/${receipt.id}`, {
        status: "approved",
        amount_eur: amount,
        ...(form.receipt_date ? { receipt_date: form.receipt_date } : {}),
        accounting_name: form.accounting_name,
        ocr: ocrPayload(),
      });
      toast.success("Račun spremljen i odobren.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spremanje nije uspjelo.");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/fuel-receipts/${receipt.id}`, {
        status: "rejected",
        ...(reason.trim() ? { rejection_reason: reason.trim() } : {}),
      });
      toast.success("Račun odbijen.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Odbijanje nije uspjelo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="receipt-verify-modal"
    >
      <div
        className="w-full max-w-6xl h-[94vh] md:h-[88vh] bg-[#0a0a0a] border border-white/10 rounded-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Provjera računa</div>
            <h3 className="text-base font-light text-white">
              {receipt.driver_name}
              {receipt.note && <span className="text-zinc-500 text-xs italic"> · "{receipt.note}"</span>}
            </h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1" data-testid="verify-close-x">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body: stacked on mobile, side-by-side on md+ */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Image panel */}
          <div className="md:w-1/2 h-[38vh] md:h-auto flex flex-col border-b md:border-b-0 md:border-r border-white/10">
            <div className="flex items-center gap-1.5 px-3 py-2 shrink-0">
              <ImgBtn onClick={() => setZoom((z) => Math.min(z + 0.25, 3))} title="Povećaj" testId="verify-zoom-in"><ZoomIn className="w-4 h-4" /></ImgBtn>
              <ImgBtn onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))} title="Smanji" testId="verify-zoom-out"><ZoomOut className="w-4 h-4" /></ImgBtn>
              <ImgBtn onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotiraj" testId="verify-rotate"><RotateCw className="w-4 h-4" /></ImgBtn>
              <span className="text-[10px] text-zinc-500 ml-1">{Math.round(zoom * 100)}%</span>
            </div>
            <div className="flex-1 overflow-auto bg-black/50 flex items-center justify-center">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Račun"
                  style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transition: "transform 0.15s ease" }}
                  className="max-w-full max-h-full object-contain"
                  data-testid="verify-image"
                />
              ) : (
                <div className="text-zinc-500 text-sm py-16">Učitavanje slike…</div>
              )}
            </div>
          </div>

          {/* Form panel */}
          <div className="md:w-1/2 flex-1 overflow-y-auto p-4 sm:p-5">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Datum">
                <input type="date" value={form.receipt_date} onChange={set("receipt_date")} className={inputCls} data-testid="verify-date" />
              </Field>
              <Field label="Ukupni iznos (€)">
                <input type="number" step="0.01" min="0" value={form.amount_eur} onChange={set("amount_eur")} className={inputCls} data-testid="verify-amount" />
              </Field>
              <Field label="PDV / Pretporez 25% (€)">
                <input type="number" step="0.01" min="0" value={form.vat_amount_eur} onChange={set("vat_amount_eur")} className={inputCls} data-testid="verify-vat" />
              </Field>
              <Field label="Način plaćanja">
                <select value={form.payment_method} onChange={set("payment_method")} className={inputCls} data-testid="verify-payment">
                  <option value="">—</option>
                  <option value="card">Kartica</option>
                  <option value="cash">Gotovina</option>
                  <option value="other">Ostalo</option>
                </select>
              </Field>
              {TEXT_FIELDS.filter((f) => !["time", "fuel_type"].includes(f.key)).map((f) => (
                <Field key={f.key} label={f.label} wide={["jir", "zki"].includes(f.key)}>
                  <input
                    value={form[f.key]} onChange={set(f.key)} placeholder={f.ph}
                    className={`${inputCls} ${f.mono ? "font-mono text-xs" : ""}`}
                    data-testid={`verify-${f.key}`}
                  />
                </Field>
              ))}
              <Field label="Vrijeme">
                <input value={form.time} onChange={set("time")} placeholder="14:35" className={inputCls} data-testid="verify-time" />
              </Field>
              <Field label="Vrsta goriva">
                <input value={form.fuel_type} onChange={set("fuel_type")} placeholder="Eurodizel" className={inputCls} data-testid="verify-fuel_type" />
              </Field>
              <Field label="Ime za knjigovodstvo" wide>
                <input
                  value={form.accounting_name} onChange={set("accounting_name")}
                  placeholder={`Zadano: ${receipt.driver_name || "vozač"}`}
                  className={inputCls}
                  list="acct-driver-names"
                  data-testid="verify-accounting_name"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Samo za izvoze knjigovođi (Excel/PDF) — vozačeva aplikacija i obračuni ostaju na originalnom imenu. Ostavite prazno za zadano.
                </p>
              </Field>
            </div>

            {rejecting && (
              <div className="mt-4 p-3 rounded-xl border border-red-500/30 bg-red-500/5">
                <p className="text-[10px] uppercase tracking-widest text-red-400 mb-2">Razlog odbijanja (opcionalno)</p>
                <input
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="npr. nečitljiv račun, duplikat…"
                  className={inputCls} autoFocus
                  data-testid="verify-reject-reason"
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-4 sm:px-6 py-3 border-t border-white/10 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-white/10 text-zinc-400 text-sm hover:text-white transition"
            data-testid="verify-cancel"
          >
            Zatvori
          </button>
          {rejecting ? (
            <button
              onClick={reject} disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/90 text-white text-sm font-semibold hover:bg-red-500 disabled:opacity-50 transition"
              data-testid="verify-reject-confirm"
            >
              <Ban className="w-4 h-4" /> Potvrdi odbijanje
            </button>
          ) : (
            <button
              onClick={() => setRejecting(true)} disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/40 text-red-400 text-sm hover:bg-red-500/10 disabled:opacity-50 transition"
              data-testid="verify-reject"
            >
              <Ban className="w-4 h-4" /> Odbij
            </button>
          )}
          <button
            onClick={approveAndSave} disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#C5A059] text-black text-sm font-semibold hover:bg-[#D4AF37] disabled:opacity-50 transition"
            data-testid="verify-approve"
          >
            <Check className="w-4 h-4" /> {busy ? "Spremanje…" : "Spremi i Odobri"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</label>
    {children}
  </div>
);

const ImgBtn = ({ children, onClick, title, testId }) => (
  <button
    onClick={onClick} title={title} data-testid={testId}
    className="p-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition"
  >
    {children}
  </button>
);
